import {v4 as uuidv4} from "uuid";
import {IDeliveryZone, IDeliveryZoneDraft} from "../../models/settings/menu";

// The backend (NewDeliveryZones) rejects the whole settings save past these limits.
export const MAX_ZONES = 50;
export const MAX_RING_VERTICES = 1000;
export const MAX_ZONES_VERTICES = 5000;
export const MAX_ZONE_NAME = 150;
export const ZONE_FALLBACK_COLOR = "#1e98ff";

export type ZonesFileError = "invalid_json" | "no_polygons" | "invalid_geometry";

/** `zone` is an index into the checked list. */
export type ZoneProblem =
    | { code: "too_many_zones" | "too_many_vertices"; count: number }
    | { code: "name_missing" | "name_too_long" | "price_missing" | "free_from_invalid" | "ring_too_small" | "coordinates_invalid"; zone: number }
    | { code: "ring_too_large"; zone: number; count: number };

// =====================================================================================================================
// GeoJSON exported from Yandex Map Constructor

/**
 * Every Polygon becomes a zone, every MultiPolygon one zone per polygon; other geometries are counted in `skipped`.
 * Rings are closed and coordinates rounded to 6 decimals (~10 cm). Limits are not enforced here: see checkDeliveryZones.
 */
export function parseConstructorGeoJSON(text: string):
    { ok: true; zones: IDeliveryZoneDraft[]; skipped: number } | { ok: false; error: ZonesFileError } {
    let json: any;
    try {
        json = JSON.parse(text.replace(/^﻿/, ""));
    } catch {
        return {ok: false, error: "invalid_json"};
    }
    const features: any[] = json?.type === "FeatureCollection" ? (Array.isArray(json.features) ? json.features : [])
        : json?.type === "Feature" ? [json]
            : [{geometry: json}];

    const zones: IDeliveryZoneDraft[] = [];
    let skipped = 0;
    for (const feature of features) {
        const type = feature?.geometry?.type;
        if (type !== "Polygon" && type !== "MultiPolygon") {
            skipped++;
            continue;
        }
        let coordinates = feature.geometry.coordinates;
        if (typeof coordinates === "string") {
            try {
                coordinates = JSON.parse(coordinates);
            } catch {
                return {ok: false, error: "invalid_geometry"};
            }
        }
        const polygons = type === "Polygon" ? [coordinates] : coordinates;
        if (!Array.isArray(polygons)) return {ok: false, error: "invalid_geometry"};

        const {name, price} = parseZoneDescription(feature.properties?.description);
        const fill = typeof feature.properties?.fill === "string" ? feature.properties.fill.trim() : "";
        for (const polygon of polygons) {
            const rings = polygonRings(polygon);
            if (!rings) return {ok: false, error: "invalid_geometry"};
            zones.push({
                id: uuidv4(),
                name: name || `Зона ${zones.length + 1}`,
                price,
                color: fill && fill.length <= 32 ? fill : undefined,
                polygon: rings,
            });
        }
    }
    return zones.length ? {ok: true, zones, skipped} : {ok: false, error: "no_polygons"};
}

function polygonRings(polygon: unknown): [number, number][][] | null {
    if (!Array.isArray(polygon) || !polygon.length) return null;
    const rings: [number, number][][] = [];
    for (const ring of polygon) {
        if (!Array.isArray(ring)) return null;
        const positions: [number, number][] = [];
        for (const position of ring) {
            if (!Array.isArray(position)) return null;
            const lon = coordinate(position[0]), lat = coordinate(position[1]);
            if (Number.isNaN(lon) || Number.isNaN(lat)) return null;
            positions.push([lon, lat]);
        }
        const first = positions[0], last = positions[positions.length - 1];
        if (first && (first[0] !== last[0] || first[1] !== last[1])) positions.push([first[0], first[1]]);
        rings.push(positions);
    }
    return rings;
}

// Constructor exports sometimes carry numbers as strings.
function coordinate(value: unknown): number {
    const n = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
    return Number.isFinite(n) ? Math.round(n * 1e6) / 1e6 : NaN;
}

const MAX_PARSED_PRICE = 100000;
const MAX_PARSED_NAME = 40;
const LETTER = "a-zа-яё";
// A whole amount: "1500" or "1 000".
const AMOUNT = "\\d{1,3}(?: \\d{3})+(?!\\d)|\\d+";
// An amount with a currency right after it ("1 000 ₸", "1500тг", "300 рублей"); the marker takes the rest of its word.
const MARKED = new RegExp(`(${AMOUNT})[^\\d${LETTER}\\n]{0,3}(?:₸|тг|тенге|kzt|руб|₽|р\\.)[${LETTER}.]*`, "gi");
// An amount glued to neither a letter nor a digit; group 1 is the character before it.
const STANDALONE = new RegExp(`(^|[^\\d${LETTER}])(${AMOUNT})(?![\\d${LETTER}])`, "gi");
const FROM_OR_UP_TO = new RegExp(`(^|[^${LETTER}])(?:от|до)\\s*$`, "i");

/**
 * Reads "name price" from a polygon description. The price is the one amount marked with a currency, else the one
 * standalone integer of at least 100. Anything ambiguous leaves the price empty for the merchant to fill: several
 * candidates, "от"/"до" before the amount, a fraction, more than 100 000. The name is the first non-empty line
 * without the price, at most 40 characters; "" when nothing remains.
 */
export function parseZoneDescription(description: unknown): { name: string; price: number | null } {
    const text = normaliseDescription(description);
    const marked = Array.from(text.matchAll(MARKED)).map((m) => ({index: m.index || 0, text: m[0], amount: m[1]}));
    const standalone = Array.from(text.matchAll(STANDALONE))
        .map((m) => ({index: (m.index || 0) + m[1].length, text: m[2], amount: m[2]}))
        .filter((c) => amountValue(c.amount) >= 100);
    const candidates = marked.length ? marked : standalone;
    if (candidates.length !== 1) return {name: zoneName(text), price: null};

    const [{index, text: fragment, amount}] = candidates;
    // "1500,50 тг" matches as "50 тг" and "1000.50" as "1000": the other half of the number goes with it
    const integerPart = /\d+[.,]$/;
    const fractionPart = /^[.,]\d+/;
    const fraction = integerPart.test(text.slice(0, index)) || fractionPart.test(text.slice(index + amount.length));
    let before = text.slice(0, index).replace(integerPart, "");
    const after = text.slice(index + fragment.length).replace(fractionPart, "");
    const qualified = FROM_OR_UP_TO.test(before);
    before = before.replace(FROM_OR_UP_TO, "$1");
    const value = amountValue(amount);
    const price = qualified || fraction || value > MAX_PARSED_PRICE ? null : value * 100;
    return {name: zoneName(before + after), price};
}

function amountValue(amount: string): number {
    return Number(amount.replace(/ /g, ""));
}

function normaliseDescription(description: unknown): string {
    const html = description == null ? "" : String(description);
    return decodeEntities(html.replace(/<br\s*\/?>|<\/(?:p|div|li)>/gi, "\n").replace(/<[^>]*>/g, " "))
        .normalize("NFKC")
        .replace(/[^\S\n]+/g, " ")
        .split("\n").map((line) => line.trim()).join("\n")
        .trim();
}

function zoneName(text: string): string {
    const line = text.split("\n")
        .map((l) => l.replace(/\(\s*\)|\[\s*]/g, " ").replace(/ +/g, " ").replace(/^[\s\-–—:;,.|/]+|[\s\-–—:;,.|/]+$/g, ""))
        .find(Boolean) || "";
    return Array.from(line).slice(0, MAX_PARSED_NAME).join("").trim();
}

const ENTITIES: { [name: string]: string } = {
    nbsp: " ", amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", laquo: "«", raquo: "»", ndash: "–", mdash: "—",
};

function decodeEntities(text: string): string {
    return text.replace(/&(#x[0-9a-f]{1,6}|#\d{1,7}|[a-z]+);/gi, (entity, body: string) => {
        if (body[0] !== "#") return ENTITIES[body.toLowerCase()] ?? entity;
        const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
        return code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    });
}

// =====================================================================================================================
// Editing

/** The wire value when every backend limit holds, otherwise every problem found. */
export function checkDeliveryZones(drafts: IDeliveryZoneDraft[]):
    { ok: true; zones: IDeliveryZone[] } | { ok: false; problems: ZoneProblem[] } {
    const problems: ZoneProblem[] = [];
    const zones: IDeliveryZone[] = [];
    if (drafts.length > MAX_ZONES) problems.push({code: "too_many_zones", count: drafts.length});
    let vertices = 0;
    drafts.forEach((draft, zone) => {
        const name = draft.name.trim();
        if (!name) problems.push({code: "name_missing", zone});
        if (Array.from(name).length > MAX_ZONE_NAME) problems.push({code: "name_too_long", zone});
        if (draft.price === null) problems.push({code: "price_missing", zone});
        if (draft.freeFrom != null && !(Number.isInteger(draft.freeFrom) && draft.freeFrom >= 0)) {
            problems.push({code: "free_from_invalid", zone});
        }

        const sizes = draft.polygon.map((ring) => ring.length);
        vertices += sizes.reduce((sum, size) => sum + Math.max(size - 1, 0), 0);
        // a closed ring is three distinct vertices plus the repeated first one
        if (!sizes.length || sizes.some((size) => size < 4)) problems.push({code: "ring_too_small", zone});
        const largest = sizes.reduce((max, size) => Math.max(max, size), 1) - 1;
        if (largest > MAX_RING_VERTICES) problems.push({code: "ring_too_large", zone, count: largest});
        if (draft.polygon.some((ring) => ring.some(([lon, lat]) => !(Math.abs(lon) <= 180 && Math.abs(lat) <= 90)))) {
            problems.push({code: "coordinates_invalid", zone});
        }

        if (draft.price !== null) zones.push({...draft, name, price: draft.price});
    });
    if (vertices > MAX_ZONES_VERTICES) problems.push({code: "too_many_vertices", count: vertices});
    return problems.length ? {ok: false, problems} : {ok: true, zones};
}

export interface ZonesReplacement {
    zones: IDeliveryZoneDraft[];
    added: string[];
    removed: string[];
    repriced: { name: string; from: number | null; to: number | null }[];
    reshaped: string[];
    unchanged: number;
}

/**
 * A re-upload replaces every zone. An incoming zone whose normalised name matches a current one keeps that zone's id,
 * so orders, which record the zone id, keep pointing at the same zone across exports, and its free-delivery threshold,
 * which the file does not carry.
 */
export function replaceZones(current: IDeliveryZoneDraft[], incoming: IDeliveryZoneDraft[]): ZonesReplacement {
    const key = (name: string) => name.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
    const unmatched = [...current];
    const result: ZonesReplacement = {zones: [], added: [], removed: [], repriced: [], reshaped: [], unchanged: 0};
    for (const zone of incoming) {
        const i = unmatched.findIndex((old) => key(old.name) === key(zone.name));
        if (i < 0) {
            result.added.push(zone.name);
            result.zones.push(zone);
            continue;
        }
        const [old] = unmatched.splice(i, 1);
        result.zones.push({...zone, id: old.id, freeFrom: old.freeFrom});
        const repriced = old.price !== zone.price;
        const reshaped = JSON.stringify(old.polygon) !== JSON.stringify(zone.polygon);
        if (repriced) result.repriced.push({name: zone.name, from: old.price, to: zone.price});
        if (reshaped) result.reshaped.push(zone.name);
        if (!repriced && !reshaped) result.unchanged++;
    }
    result.removed = unmatched.map((zone) => zone.name);
    return result;
}

// =====================================================================================================================
// Geometry

// The even-odd rule over all rings, as in the backend's DeliveryZone.Contains, so holes and self-intersections
// resolve alike.
function evenOdd(polygon: [number, number][][], [lon, lat]: [number, number]): boolean {
    let inside = false;
    for (const ring of polygon) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const [aLon, aLat] = ring[j], [bLon, bLat] = ring[i];
            if ((aLat > lat) !== (bLat > lat) && lon < (bLon - aLon) * (lat - aLat) / (bLat - aLat) + aLon) inside = !inside;
        }
    }
    return inside;
}

/** Metres from p to the nearest edge, on a flat projection around p: exact enough at city scale. */
function borderDistance(polygon: [number, number][][], [lon, lat]: [number, number]): number {
    const my = 111320, mx = my * Math.cos(lat * Math.PI / 180);
    let best = Infinity;
    for (const ring of polygon) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const ax = (ring[j][0] - lon) * mx, ay = (ring[j][1] - lat) * my;
            const dx = (ring[i][0] - ring[j][0]) * mx, dy = (ring[i][1] - ring[j][1]) * my;
            const t = dx || dy ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / (dx * dx + dy * dy))) : 0;
            best = Math.min(best, Math.hypot(ax + t * dx, ay + t * dy));
        }
    }
    return best;
}

// Hand-drawn neighbours overlap along their shared border by a few metres to a few dozen; the cheaper zone wins there
// and nobody notices. Deeper than this, a zone's price is hidden over an area guests actually order from.
const OVERLAP_DEPTH_M = 50;

/**
 * Index pairs of zones where a vertex of one lies more than OVERLAP_DEPTH_M inside the other. Cheap and incomplete:
 * two zones crossing like a plus sign, with no vertex inside the other, are missed.
 */
export function overlappingZones(zones: IDeliveryZoneDraft[]): [number, number][] {
    const inside = (outer: IDeliveryZoneDraft, inner: IDeliveryZoneDraft) => inner.polygon.some((ring) =>
        ring.some((p) => evenOdd(outer.polygon, p) && borderDistance(outer.polygon, p) > OVERLAP_DEPTH_M));
    const pairs: [number, number][] = [];
    zones.forEach((a, i) => zones.forEach((b, j) => {
        if (i < j && (inside(a, b) || inside(b, a))) pairs.push([i, j]);
    }));
    return pairs;
}

/** [[south, west], [north, east]] in the Yandex map's [lat, lon] order; undefined without points. */
export function zonesBounds(zones: IDeliveryZoneDraft[]): number[][] | undefined {
    let south = Infinity, west = Infinity, north = -Infinity, east = -Infinity;
    for (const zone of zones) {
        for (const [lon, lat] of zone.polygon[0] || []) {
            south = Math.min(south, lat); north = Math.max(north, lat);
            west = Math.min(west, lon); east = Math.max(east, lon);
        }
    }
    return south <= north ? [[south, west], [north, east]] : undefined;
}

/** What a price input accepts while typing: whole major units with an optional 2-digit fraction. */
export const MAJOR_UNITS_INPUT = /^\d{0,9}(?:[.,]\d{0,2})?$/;

/** "1500", "1500,5" → minor units; "" → null. Expects text matching MAJOR_UNITS_INPUT. */
export function majorToMinor(text: string): number | null {
    const n = parseFloat(text.replace(",", "."));
    return Number.isFinite(n) ? Math.round(n * 100) : null;
}

export function minorToMajor(price: number | null): string {
    return price === null ? "" : String(price / 100);
}
